import { PaymentMethod } from 'constants/payment';
import * as IBAN from 'iban';
import Input from 'components/ui/Form/Input';
import config from 'config';
import { useTranslation } from 'hooks/useTranslation';
import styles from './PaymentFormStep1.module.scss';
import type { PaymentFormStepBaseProps } from 'types/Payment';
import type { Ref } from 'react';

interface PaymentFormStepProps
  extends Omit<PaymentFormStepBaseProps, 'register'> {
  register: ({
    required,
    pattern,
    maxLength,
    minLength,
    validate,
  }: {
    required?: boolean;
    pattern?: { value: RegExp; message: string };
    maxLength?: number;
    minLength?: number;
    validate?: Record<string, unknown>;
  }) => Ref<HTMLInputElement>;
}

const PaymentFormStep1 = ({
  method,
  formData,
  register,
  errors,
}: PaymentFormStepProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div>
      <Input
        groupClasses={styles.inputGroupStyles}
        label={t('referAFriend.rewardForm.accountHolderName')}
        isRequired
        type="text"
        name="accountHolderName"
        required
        placeholder={t('referAFriend.rewardForm.accountHolderName.placeholder')}
        ref={register({
          required: true,
          maxLength: 64,
          minLength: 3,
        })}
        error={
          errors['accountHolderName'] &&
          t('referAFriend.rewardForm.accountHolderName.error')
        }
        className={styles.inputStyles}
        defaultValue={formData['accountHolderName']}
      />
      {method === PaymentMethod.BANK ? (
        <>
          <Input
            groupClasses={styles.inputGroupStyles}
            label={t('referAFriend.rewardForm.iban')}
            isRequired
            type="text"
            name="iban"
            required
            placeholder="ZY00 0000 0000 0000 0000 00"
            ref={register({
              required: true,
              maxLength: 64,
              minLength: 3,
              validate: {
                isIban: (value: string) =>
                  !!IBAN.isValid(value) ||
                  t('referAFriend.rewardForm.iban.invalidError'),
              },
            })}
            error={
              errors['iban']
                ? errors.iban.message || t('referAFriend.rewardForm.iban.error')
                : null
            }
            className={styles.inputStyles}
            defaultValue={formData['iban']}
          />
          <Input
            groupClasses={styles.inputGroupStyles}
            label={t('referAFriend.rewardForm.bic')}
            type="text"
            name="bic"
            placeholder="ABCXYZ0000"
            ref={register({
              required: false,
              maxLength: 11,
              minLength: 8,
            })}
            error={errors['bic'] && t('referAFriend.rewardForm.bic.error')}
            className={styles.inputStyles}
            defaultValue={formData['bic']}
          />
        </>
      ) : (
        <Input
          groupClasses={styles.inputGroupStyles}
          label={t('referAFriend.rewardForm.paypalEmail')}
          isRequired
          type="email"
          name="paypalEmail"
          required
          placeholder={t('referAFriend.rewardForm.paypalEmail')}
          ref={register({
            required: true,
            maxLength: 64,
            minLength: 3,
            pattern: {
              value: config.defaults.emailRegex,
              message: t('form.error.emailIsInvalid'),
            },
          })}
          error={
            errors['paypalEmail']
              ? errors.paypalEmail.message ||
                t('referAFriend.rewardForm.paypalEmail.error')
              : null
          }
          className={styles.inputStyles}
          defaultValue={formData['paypalEmail']}
        />
      )}
    </div>
  );
};

export default PaymentFormStep1;
